import UserSessionStarted from '../actions/UserSessionStarted.js';
import { getUserGroup } from '../identity/getUserGroup.js';
import { skipIdentify } from '../identity/skipIdentify.js';
import LOG from '../logger.js';
import { getItem, setItem } from '../storage/jsonStorage.js';
import { isSerializable } from '../types.js';
import { getReporters } from './reporters.js';
import { track } from './track.js';
export const USER_TRAITS_KEY = 'CVENT_ANALYTICS_USER_TRAITS';
/**
 * Function to merge the customUserTraits to allow consumers to set custom traits without overriding
 * existing values
 *
 * @param existingTraits The traits that are already stored in the local storage
 * @param traits  The new traits used to identify the user
 */
function mergeCustomTraits(existingTraits, traits) {
    const customTraits = {};
    const existingCustomTraits = existingTraits?.customTraits || {};
    const newCustomTraits = traits.customTraits || {};
    const keys = Array.from(new Set([...Object.keys(existingCustomTraits), ...Object.keys(newCustomTraits)]));
    keys.forEach(key => {
        customTraits[key] = {
            ...existingCustomTraits[key],
            ...newCustomTraits[key]
        };
    });
    return Object.keys(customTraits).length > 0 ? customTraits : undefined;
}
/**
 * Identifies user in the data platform. If the user logged in via CVII or is an automation user we
 * skip the identify call to avoid merging profiles.
 *
 * Implementation Notes: I have intentionally left out the following features:
 *   - Tracking if changes to identify should be applied; only allowing it when things have truly changed
 *   - Encrypting data in local storage - encrypting with the key available on the browser is somewhat pointless
 *   - Sending identification of automation users to Snowflake, but not other destinations
 */
export function identify(traits) {
    // if this is an internal user - usually users used for synthetic tests, cvii workflows, or automation tests
    if (skipIdentify(traits)) {
        LOG.debug('Internal User - skipping identify');
        return;
    }
    // read user trait from local storage
    const existingTraits = getItem(USER_TRAITS_KEY);
    // Get the user's group
    const group = getUserGroup(traits);
    const customTraits = mergeCustomTraits(existingTraits, traits);
    // combine existing traits with new traits.
    const userTraits = {
        ...existingTraits,
        ...traits,
        customTraits,
        group
    };
    // Update user trait in storage
    setUserTraits(userTraits);
    // Call the identify method on reporters
    const reporters = getReporters();
    for (const reporter of reporters.values()) {
        reporter.identify(userTraits);
    }
    // Send identification action to the backend using track()
    track(new UserSessionStarted({}));
}
export function getUserTraits() {
    return getItem(USER_TRAITS_KEY);
}
export function setUserTraits(userTraits) {
    if (isSerializable(userTraits)) {
        setItem(USER_TRAITS_KEY, userTraits);
    }
    else {
        LOG.debug('User traits are not serializable, skipping setItem', userTraits);
    }
}
//# sourceMappingURL=identify.js.map